import getBrowserLogger from '@zg-rentals/logger-browser';
import type { Logger, LoggerOptions } from '@zg-rentals/logger-browser';
import type { Plugin } from '@zg-rentals/rp-bootstrap-base';
import webVitals from './webVitals';
import { Bootstrap, BootstrapBuilder } from '@zg-rentals/rp-bootstrap-base';
import { BrowserTracer } from '@zg-rentals/trace/client';
import { DragnetBrowser, dragnetReporter } from '@zg-rentals/dragnet-browser';
import { MarlinBrowser, marlinReporter } from '@zg-rentals/marlin-browser';
import { getGlobalMonitor, Monitor, type Reporter } from '@zg-rentals/monitor-base';
import { classifyClientNoise } from './classifyClientNoise';
import { isBrowserExtensionError, isBrowserExtensionRejection } from './isBrowserExtensionError';
import { isDev } from '@zg-rentals/environment-utils';
import { logError, setLogErrorBeforeSend } from '@zg-rentals/log-error';

export class BootstrapBrowserBuilder extends BootstrapBuilder {
  private marlinEnabled = true;
  private loggerOptions: LoggerOptions = {};
  private initializeLogger = (logger: Logger) => logger;

  withoutMarlin(): this {
    this.marlinEnabled = false;
    return this;
  }

  withLoggerOptions(loggerOptions: LoggerOptions, initializeLogger?: (logger: Logger) => Logger) {
    this.loggerOptions = loggerOptions;
    if (initializeLogger) {
      this.initializeLogger = initializeLogger;
    }
    return this;
  }

  withHostedAppLoggerOptions(hostedApp: string, level = 'warn') {
    return this.withLoggerOptions({ level, logPath: `/${hostedApp}/hostedApp/log?appName=${this.appName}` });
  }

  withRentalHostedAppLoggerOptions(hostedApp: string, level = 'warn') {
    return this.withLoggerOptions({ level, logPath: `/rental/${hostedApp}/hostedApp/log?appName=${this.appName}` });
  }

  private readonly plugins: Array<Plugin> = [webVitals];
  withPlugin(plugin: Plugin) {
    this.plugins.push(plugin);
    return this;
  }

  private onRegisterCallbacks: Array<() => void> = [];
  withOnRegister(callback: () => void) {
    this.onRegisterCallbacks.push(callback);
    return this;
  }

  private getReporters(): Array<(logger: Logger) => Reporter> {
    const reporters: Array<(logger: Logger) => Reporter> = [];

    if (this.marlinEnabled) {
      reporters.push(() => marlinReporter(new MarlinBrowser(this.appName), this.appName, 'browser'));
    }

    reporters.push((logger) =>
      dragnetReporter(
        new DragnetBrowser(this.appName, this.buildNumber),
        logger.child({ name: 'dragnet-browser-reporter' }),
      ),
    );

    return reporters;
  }

  build() {
    const appBeforeSend = this.logErrorBeforeSend;
    setLogErrorBeforeSend((event) =>
      classifyClientNoise(event).match({
        Just: (kind) => {
          getGlobalMonitor().count({ name: `browser filtered error ${kind}` });
          return false;
        },
        Nothing: () => appBeforeSend?.(event),
      }),
    );
    return new BootstrapBrowser(
      this.appName,
      this.buildNumber,
      this.plugins,
      { ...this.loggerOptions, initializeLogger: this.initializeLogger },
      this.getReporters(),
      this.onRegisterCallbacks,
    );
  }
}

export class BootstrapBrowser extends Bootstrap<BrowserTracer> {
  constructor(
    appName: string,
    buildNumber: number,
    plugins: Array<Plugin>,
    loggerOptions: LoggerOptions & { initializeLogger: (logger: Logger) => Logger },
    reporters: Array<(logger: Logger) => Reporter>,
    onRegisterCallbacks: Array<() => void>,
  ) {
    const logger = loggerOptions.initializeLogger(
      getBrowserLogger({
        name: appName,
        ...loggerOptions,
        logPath: isDev() ? undefined : loggerOptions.logPath,
      }),
    );

    const monitor = new Monitor(reporters.map((reporter) => reporter(logger)));

    const tracer = new BrowserTracer();

    super(appName, buildNumber, plugins, logger, monitor, tracer);

    this.onRegister(() => {
      monitor.count({ name: 'browser init' });
      monitor.count({ name: `buildNumber browser ${this.buildNumber}` });

      tracer.init();

      window.addEventListener('error', ({ error, filename, lineno, colno }) => {
        if (error != null && !isBrowserExtensionError(filename)) {
          logError({
            error,
            context: {
              filename,
              lineNumber: lineno,
              columnNumber: colno,
            },
          });
        }
      });

      window.addEventListener('unhandledrejection', ({ reason }) => {
        if (!isBrowserExtensionRejection(reason)) {
          logError({
            error: reason instanceof Error ? reason : new Error(`${reason}`),
            errorType: 'unhandledRejection',
          });
        }
      });

      onRegisterCallbacks.forEach((callback) => callback());
    });
  }
}

export { Bootstrap };
